/**
 * ThemeContext - Theme state management
 *
 * Supports exactly two themes: 'dark' (default) and 'light'.
 * Persists user choice to localStorage and applies via `data-theme` on <html>.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'glowpop_theme';
const THEMES = ['dark', 'light'];
const DEFAULT_THEME = 'dark';

// HMR-resilient context: Store on globalThis to survive hot reloads
const CONTEXT_KEY = '__GLOWPOP_THEME_CONTEXT__';
if (!globalThis[CONTEXT_KEY]) {
  globalThis[CONTEXT_KEY] = createContext(null);
}
const ThemeContext = globalThis[CONTEXT_KEY];

const loadStoredTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (THEMES.includes(stored)) return stored;
    // Normalize legacy values (system, dark-glass, dark-luxe, anything else) to default.
    if (stored != null) {
      try { localStorage.setItem(STORAGE_KEY, DEFAULT_THEME); } catch { /* ignore */ }
    }
  } catch (e) {
    console.warn('Failed to load theme preference:', e);
  }
  return DEFAULT_THEME;
};

const saveTheme = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    console.warn('Failed to save theme preference:', e);
  }
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(loadStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    if (!THEMES.includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}`);
      return;
    }
    setThemeState(newTheme);
    saveTheme(newTheme);
  }, []);

  const value = {
    theme,
    resolvedTheme: theme,
    setTheme,
    themes: THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme - Access theme context
 *
 * Usage:
 *   const { theme, resolvedTheme, setTheme, themes } = useTheme();
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
